import { useMemo, useState, type ReactNode } from "react";
import { Download, SlidersHorizontal } from "lucide-react";

import { useT } from "../../i18n";
import { plural } from "../../i18n/plural";
import { useLocaleStore } from "../../store/localeStore";
import type { DatePreset } from "../../store/filtersStore";
import type { Satellite } from "../../data/types";
import { SATELLITE_OPTIONS } from "../../data/satellites";
import { cx } from "../../lib/cx";
import { countChanges, useFiltersStore } from "../../store/filtersStore";
import { useDataStore } from "../../store/dataStore";
import { useLayersStore } from "../../store/layersStore";
import { Checkbox } from "../ui/Checkbox";
import { ExportModal } from "../export/ExportModal";
import { FloatingPanel } from "../layout/FloatingPanel";

const SATELLITES: Satellite[] = ["sentinel-2", "landsat-8", "landsat-9", "emit"];
const RATE_MAX = 4000;

/**
 * Preset keys and the dictionary key each one is labelled by. The label is
 * resolved at render, not here: a module constant is built once at import and
 * would keep whichever language was active then.
 */
type PresetLabelKey = "presetD7" | "presetD30" | "presetD90" | "presetY1" | "presetAll";

const PRESETS: { key: DatePreset; labelKey: PresetLabelKey }[] = [
  { key: "7d", labelKey: "presetD7" },
  { key: "30d", labelKey: "presetD30" },
  { key: "90d", labelKey: "presetD90" },
  { key: "1y", labelKey: "presetY1" },
  { key: "all", labelKey: "presetAll" },
];

function GroupTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary first:mt-0">
      {children}
    </h3>
  );
}

export function FiltersPanel() {
  const t = useT();
  const locale = useLocaleStore((s) => s.locale);
  const range = useFiltersStore((s) => s.range);
  const draft = useFiltersStore((s) => s.draft);
  const applied = useFiltersStore((s) => s.applied);
  const setDraftPeriod = useFiltersStore((s) => s.setDraftPeriod);
  const applyPreset = useFiltersStore((s) => s.applyPreset);
  const setDraftSatellites = useFiltersStore((s) => s.setDraftSatellites);
  const setMinRate = useFiltersStore((s) => s.setMinRate);
  const apply = useFiltersStore((s) => s.apply);
  const reset = useFiltersStore((s) => s.reset);

  const records = useDataStore((s) => s.records);
  const grades = useLayersStore((s) => s.grades);
  const [exportOpen, setExportOpen] = useState(false);

  const changes = countChanges(draft, applied);

  // Per-satellite detection counts under the current period / min-rate / grade
  // context, ignoring the satellite selection itself.
  const counts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const sat of SATELLITES) acc[sat] = 0;
    for (const rec of records) {
      if (grades.length && !grades.includes(rec.grade)) continue;
      if (draft.from && rec.date < draft.from) continue;
      if (draft.to && rec.date > draft.to) continue;
      if (draft.minRate > 0 && (rec.emissionRate === null || rec.emissionRate < draft.minRate)) {
        continue;
      }
      acc[rec.satellite] = (acc[rec.satellite] ?? 0) + 1;
    }
    return acc;
  }, [records, grades, draft.from, draft.to, draft.minRate]);

  return (
    <FloatingPanel
      icon={<SlidersHorizontal size={16} />}
      title={t.filters.title}
      openLabel={t.filters.open}
      collapseLabel={t.filters.collapse}
      defaultOpen
      widthClass="w-72"
    >
      <GroupTitle>{t.filters.period}</GroupTitle>
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={draft.from}
          min={range.min || undefined}
          max={draft.to || range.max || undefined}
          onChange={(e) => setDraftPeriod(e.target.value, draft.to)}
          className="w-full rounded border border-border-default bg-surface-sunken px-2 py-1 text-sm text-text-primary"
        />
        <span className="text-text-tertiary">—</span>
        <input
          type="date"
          value={draft.to}
          min={draft.from || range.min || undefined}
          max={range.max || undefined}
          onChange={(e) => setDraftPeriod(draft.from, e.target.value)}
          className="w-full rounded border border-border-default bg-surface-sunken px-2 py-1 text-sm text-text-primary"
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => applyPreset(p.key)}
            className="rounded-full border border-border-default px-2 py-0.5 text-xs text-text-secondary transition-colors hover:border-accent-border hover:text-accent"
          >
            {t.filters[p.labelKey]}
          </button>
        ))}
      </div>

      <GroupTitle>{t.filters.satellites}</GroupTitle>
      {SATELLITE_OPTIONS.map((opt) => {
        // A grouped option (Landsat 8/9) is checked only when all its
        // satellites are selected; toggling adds or removes them together.
        const checked = opt.sats.every((s) => draft.satellites.includes(s));
        const groupCount = opt.sats.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
        const toggle = () => {
          const next = new Set(draft.satellites);
          if (checked) opt.sats.forEach((s) => next.delete(s));
          else opt.sats.forEach((s) => next.add(s));
          setDraftSatellites([...next]);
        };
        return (
          <Checkbox
            key={opt.key}
            checked={checked}
            onChange={toggle}
            label={opt.label}
            trailing={
              <span className="data-value text-xs text-text-tertiary">{groupCount}</span>
            }
          />
        );
      })}

      <GroupTitle>{t.filters.minRate}</GroupTitle>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={RATE_MAX}
          step={50}
          value={draft.minRate}
          onChange={(e) => setMinRate(Number(e.target.value))}
          className="data-value w-20 rounded border border-border-default bg-surface-sunken px-2 py-1 text-sm text-text-primary"
        />
        <input
          type="range"
          min={0}
          max={RATE_MAX}
          step={50}
          value={draft.minRate}
          onChange={(e) => setMinRate(Number(e.target.value))}
          className="flex-1 accent-accent"
          aria-label={t.filters.minRate}
        />
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={apply}
          disabled={changes === 0}
          className={cx(
            "flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
            changes === 0
              ? "cursor-not-allowed bg-surface-sunken text-text-disabled"
              : "bg-accent text-text-onAccent hover:bg-accent-hover",
          )}
        >
          {changes === 0 ? t.filters.apply : `${t.filters.apply} · ${plural(locale, changes, t.filters.changesPlural)}`}
        </button>
        <button
          type="button"
          onClick={reset}
          className="rounded-lg border border-border-default px-3 py-1.5 text-sm text-text-secondary transition-colors hover:border-border-strong hover:text-text-primary"
        >
          {t.filters.reset}
        </button>
      </div>

      <button
        type="button"
        onClick={() => setExportOpen(true)}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-border-default px-3 py-1.5 text-sm text-text-secondary transition-colors hover:border-accent-border hover:text-accent"
      >
        <Download size={15} />
        {t.filters.export}
      </button>

      {exportOpen && <ExportModal onClose={() => setExportOpen(false)} />}
    </FloatingPanel>
  );
}
